import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";

import { Table, TableBody, TableCell, TableHead, TableRow } from "ui";

import { useGetVersionQuery } from "fond/api";
import { useFeatureFlag } from "fond/featureFlags";
import { useCanICollaboration } from "fond/hooks/useCanICollaboration";
import { useGetScenarioConfiguration } from "fond/hooks/useGetScenarioConfiguration";
import BomModal from "fond/project/bom/BomModal";
import { load } from "fond/project/bom/redux";
import { Project } from "fond/types";
import { formatCurrency } from "fond/utils/currency";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { isCollaborationProject } from "fond/utils/project";
import { LinkButton } from "fond/widgets/Link/LinkButton";

import { BomCarvePanel } from "./BomCarvePanel";

const stageConfig: Record<string, { label: string }> = {
  partition: { label: "Partitioning BOM" },
};

type ScenarioPanelBomSectionProps = {
  project: Project;
};

export const ScenarioPanelBomSection: React.FC<ScenarioPanelBomSectionProps> = ({ project }) => {
  const dispatch = useAppDispatch();
  const { value: canAccessGridBom } = useFeatureFlag("grid_bom");
  const { canCarveBOM, isLoading: isLoadingCollaboration, isDisabled } = useCanICollaboration();
  const versionId = useAppSelector((state) => state.project.versionId);
  const { configuration: scenarioConfiguration, isLoading: isLoadingScenarioConfiguration } = useGetScenarioConfiguration({ projectId: project.ID });
  const { data: version } = useGetVersionQuery(versionId, { skip: !versionId });
  const bom = useAppSelector((state) => state.bom.bom);
  const [showBomModal, setShowBomModal] = useState(false);
  const architecture = version?.Architecture ?? null;

  useEffect(() => {
    dispatch(load(versionId));
  }, [versionId, dispatch]);

  const showBomModalClick = () => {
    setShowBomModal(true);
  };

  if (!bom || isLoadingCollaboration || isLoadingScenarioConfiguration || !isCollaborationProject(project)) return null;

  return (
    <Box mt={2}>
      <Table variant="ag-grid-compact">
        <TableHead>
          <TableRow>
            <TableCell>Bill of Materials</TableCell>
            <TableCell align="right">{canAccessGridBom && <LinkButton onClick={showBomModalClick}>View</LinkButton>}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell>Total estimated cost</TableCell>
            <TableCell align="right">
              <b>{formatCurrency(bom.total, { currency: scenarioConfiguration?.Currency })}</b>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {showBomModal && <BomModal project={project} architecture={architecture} onClose={() => setShowBomModal(false)} />}
      {canCarveBOM && <BomCarvePanel versionId={versionId} isDisabled={isDisabled} />}
    </Box>
  );
};
